import React, { useEffect, useState } from "react";
import { PrintButton } from "../components/PrintButton";
import { BulkImportDialog } from "../components/BulkImportDialog";
import { getCertificates, createCertificate, updateCertificate, deleteCertificate, bulkCreateCertificates } from "../lib/api";
import { toast } from "sonner";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import { Input } from "../components/ui/input";
import {
  ShieldAlert, ChevronRight, Plus, Trash2, Save, X, AlertTriangle, CalendarClock, FileUp,
} from "lucide-react";

// Days until a YYYY-MM-DD date (null if unparseable). Negative = past.
function daysUntil(dateStr) {
  if (!dateStr) return null;
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d.getTime())) return null;
  return Math.ceil((d - new Date().setHours(0, 0, 0, 0)) / 86400000);
}

// Expiry health from a date.
function certStatus(dateStr) {
  const d = daysUntil(dateStr);
  if (d === null) return { key: "unknown", label: "No date", cls: "bg-zinc-500/10 text-zinc-400 border-zinc-500/20" };
  if (d < 0) return { key: "expired", label: "Expired", cls: "bg-red-500/10 text-red-400 border-red-500/20", days: d };
  if (d <= 30) return { key: "critical", label: `${d}d left`, cls: "bg-red-500/10 text-red-400 border-red-500/20", days: d };
  if (d <= 90) return { key: "soon", label: `${d}d left`, cls: "bg-amber-500/10 text-amber-400 border-amber-500/20", days: d };
  return { key: "ok", label: "Valid", cls: "bg-emerald-500/10 text-emerald-400 border-emerald-500/20", days: d };
}

const CERT_TYPES = ["Tomcat", "CallManager", "CAPF", "TVS", "IPSec", "ITLRecovery", "TFTP", "Expressway Server", "Other"];
const TYPE_CLS = {
  Tomcat: "text-blue-400", CallManager: "text-purple-400", CAPF: "text-teal-400",
  TVS: "text-amber-400", IPSec: "text-emerald-400", "Expressway Server": "text-pink-400",
};

export default function Certificates() {
  const [certs, setCerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [form, setForm] = useState({});
  const [adding, setAdding] = useState(false);
  const [showImport, setShowImport] = useState(false);
  const [newCert, setNewCert] = useState({ name: "", node: "", cert_type: "Tomcat", issued_to: "", issuer: "", expires: "" });

  const load = () => getCertificates().then((d) => { setCerts(d); setLoading(false); }).catch(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const startEdit = (c) => { setEditId(c.id); setForm({ ...c }); };
  const saveEdit = async () => {
    try {
      const updated = await updateCertificate(editId, {
        node: form.node || "",
        cert_type: form.cert_type,
        issued_to: form.issued_to || "",
        issuer: form.issuer || "",
        expires: form.expires || "",
        notes: form.notes || "",
      });
      setCerts((prev) => prev.map((c) => (c.id === editId ? updated : c)));
      setEditId(null);
      toast.success("Certificate updated");
    } catch { toast.error("Update failed"); }
  };

  const addCert = async () => {
    if (!newCert.name.trim()) { toast.error("Enter a certificate name"); return; }
    try {
      const created = await createCertificate(newCert);
      setCerts((prev) => [...prev, created]);
      setAdding(false);
      setNewCert({ name: "", node: "", cert_type: "Tomcat", issued_to: "", issuer: "", expires: "" });
      toast.success("Certificate added");
    } catch { toast.error("Add failed"); }
  };

  const remove = async (id) => {
    try { await deleteCertificate(id); setCerts((prev) => prev.filter((c) => c.id !== id)); toast.success("Deleted"); }
    catch { toast.error("Delete failed"); }
  };

  const expired = certs.filter((c) => certStatus(c.expires).key === "expired").length;
  const critical = certs.filter((c) => certStatus(c.expires).key === "critical").length;
  const soon = certs.filter((c) => certStatus(c.expires).key === "soon").length;
  const untracked = certs.filter((c) => certStatus(c.expires).key === "unknown").length;

  // Sort so the most urgent surface first.
  const rank = { expired: 0, critical: 1, soon: 2, ok: 3, unknown: 4 };
  const sorted = [...certs].sort((a, b) => rank[certStatus(a.expires).key] - rank[certStatus(b.expires).key]);

  return (
    <div>
      <header className="h-16 border-b border-zinc-800 flex items-center px-5 sm:px-8 bg-zinc-950/60 backdrop-blur lg:sticky lg:top-0 z-10">
        <span className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold flex items-center gap-2">
          <span className="text-zinc-600">~/</span> certificates <ChevronRight className="h-3 w-3" /> expiry
        </span>
      </header>

      <div className="p-5 sm:p-8 max-w-5xl">
        <div className="no-print flex justify-end gap-2 mb-4">
          <button onClick={() => setShowImport(true)} data-testid="import-certificates-btn" className="flex items-center gap-1.5 text-[12px] font-semibold px-3 py-1.5 rounded-md border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors">
            <FileUp className="h-3.5 w-3.5" /> Import CSV
          </button>
          <PrintButton />
        </div>
        <BulkImportDialog
          open={showImport} onOpenChange={setShowImport} testid="import-certificates"
          title="Bulk import certificates"
          description="Upload a CSV or paste rows to add CUCM/Expressway certificates in bulk."
          columns={[
            { key: "name", synonyms: ["certificate", "cert"] },
            { key: "node", synonyms: ["server", "host"] },
            { key: "cert_type", synonyms: ["type", "cert type"] },
            { key: "issued_to", synonyms: ["subject", "cn", "issued to"] },
            { key: "issuer", synonyms: ["ca", "issued by"] },
            { key: "expires", synonyms: ["expiry", "expiration", "valid to", "expires on"] },
            { key: "notes", synonyms: ["note", "comment"] },
          ]}
          sampleCsv={"name,node,cert_type,issued_to,issuer,expires,notes\nCUCM Pub Tomcat,cucm-pub,Tomcat,cucm-pub.corp.local,Enterprise-CA,2026-08-01,\nExpressway-E,expe-1,Expressway Server,mra.corp.com,Public-CA,2026-05-20,"}
          sampleFilename="certificates_template.csv"
          onImport={bulkCreateCertificates}
          onDone={load}
        />
        <div className="flex items-center gap-2 text-[11px] uppercase tracking-widest text-red-400 font-semibold">
          <ShieldAlert className="h-4 w-4" /> Certificate Expiry Tracker
        </div>
        <h1 className="font-heading text-4xl sm:text-5xl font-extrabold text-white tracking-tight mt-2">CUCM &amp; UC Certificates</h1>
        <p className="text-zinc-400 mt-3 max-w-2xl">
          Expired Tomcat, CallManager, CAPF or TVS certificates are a top cause of phone-registration failures,
          secure-SIP/TLS outages and RTMT alarms. Track every platform &amp; service cert and its renewal date.
        </p>

        {/* Summary */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-6">
          <SummaryStat label="Expired" value={expired} testId="cert-stat-expired" tone={expired > 0 ? "red" : "default"} />
          <SummaryStat label="≤30 days" value={critical} testId="cert-stat-critical" tone={critical > 0 ? "red" : "default"} />
          <SummaryStat label="≤90 days" value={soon} testId="cert-stat-soon" tone={soon > 0 ? "amber" : "default"} />
          <SummaryStat label="No date set" value={untracked} testId="cert-stat-untracked" tone={untracked > 0 ? "amber" : "default"} />
        </div>

        {(expired > 0 || critical > 0) && (
          <div data-testid="cert-alert-banner" className="mt-4 flex items-start gap-2.5 rounded-lg border border-red-500/30 bg-red-500/10 px-4 py-3">
            <AlertTriangle className="h-4 w-4 text-red-400 mt-0.5 shrink-0" />
            <div className="text-[13px] text-red-200">
              <span className="font-semibold">{expired + critical} certificate{expired + critical > 1 ? "s" : ""}</span> expired or expiring within 30 days.
              Regenerate via <span className="font-mono text-red-300">Cisco Unified OS Admin &gt; Security &gt; Certificate Management</span> and restart affected services.
            </div>
          </div>
        )}

        {/* Table */}
        <div className="mt-6 rounded-lg border border-zinc-800 bg-zinc-900 overflow-hidden">
          <div className="hidden md:grid grid-cols-[1.6fr_0.9fr_1.2fr_1fr_1fr_auto] gap-3 px-4 py-2.5 border-b border-zinc-800 text-[10px] uppercase tracking-widest text-zinc-500 font-semibold">
            <span>Certificate</span><span>Type</span><span>Node / CN</span><span>Expires</span><span>Status</span><span></span>
          </div>

          {loading ? (
            <div className="p-6 text-zinc-500 font-mono text-sm">Loading certificates…</div>
          ) : (
            <div className="divide-y divide-zinc-800" data-testid="certificates-list">
              {sorted.map((c) => {
                const editing = editId === c.id;
                const st = certStatus(c.expires);
                return (
                  <div key={c.id} data-testid={`cert-row-${c.id}`} className="grid grid-cols-2 md:grid-cols-[1.6fr_0.9fr_1.2fr_1fr_1fr_auto] gap-3 px-4 py-3 items-center">
                    <div>
                      <div className="text-sm text-white font-medium">{c.name}</div>
                      <div className="text-[11px] text-zinc-500">{c.issuer || "—"}</div>
                    </div>
                    <div className={`text-[12px] font-semibold ${TYPE_CLS[c.cert_type] || "text-zinc-400"}`}>{c.cert_type}</div>

                    {editing ? (
                      <>
                        <Input data-testid={`edit-cert-cn-${c.id}`} value={form.issued_to || ""} onChange={(e) => setForm({ ...form, issued_to: e.target.value })} placeholder="CN / node" className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Input data-testid={`edit-cert-expires-${c.id}`} value={form.expires || ""} onChange={(e) => setForm({ ...form, expires: e.target.value })} placeholder="YYYY-MM-DD" className="h-8 bg-zinc-950 border-zinc-800 text-sm" />
                        <Select value={form.cert_type} onValueChange={(v) => setForm({ ...form, cert_type: v })}>
                          <SelectTrigger data-testid={`edit-cert-type-${c.id}`} className="h-8 bg-zinc-950 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                          <SelectContent className="bg-zinc-900 border-zinc-800">
                            {CERT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}
                          </SelectContent>
                        </Select>
                        <div className="flex items-center gap-1.5">
                          <button onClick={saveEdit} data-testid={`save-cert-${c.id}`} className="h-8 w-8 flex items-center justify-center rounded-md bg-blue-600 hover:bg-blue-700 text-white"><Save className="h-4 w-4" /></button>
                          <button onClick={() => setEditId(null)} className="h-8 w-8 flex items-center justify-center rounded-md bg-zinc-800 hover:bg-zinc-700 text-zinc-300"><X className="h-4 w-4" /></button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div className="text-[13px] text-zinc-300 font-mono truncate">{c.issued_to || c.node || "—"}</div>
                        <div className={`text-[13px] font-mono flex items-center gap-1 ${st.key === "expired" || st.key === "critical" ? "text-red-400" : st.key === "soon" ? "text-amber-400" : "text-zinc-400"}`}>
                          {(st.key === "expired" || st.key === "critical" || st.key === "soon") && <CalendarClock className="h-3.5 w-3.5" />}
                          {c.expires || "—"}
                        </div>
                        <div><span className={`text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full border ${st.cls}`}>{st.label}</span></div>
                        <div className="flex items-center gap-1.5">
                          <button onClick={() => startEdit(c)} data-testid={`edit-cert-${c.id}`} className="text-[11px] text-blue-400 hover:underline">Edit</button>
                          <button onClick={() => remove(c.id)} data-testid={`delete-cert-${c.id}`} className="h-7 w-7 flex items-center justify-center rounded-md text-zinc-500 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                        </div>
                      </>
                    )}
                  </div>
                );
              })}
            </div>
          )}

          {/* Add row */}
          {adding ? (
            <div className="p-4 border-t border-zinc-800 bg-zinc-950/50" data-testid="add-cert-form">
              <div className="grid grid-cols-1 sm:grid-cols-6 gap-2">
                <Input data-testid="new-cert-name" value={newCert.name} onChange={(e) => setNewCert({ ...newCert, name: e.target.value })} placeholder="Certificate name" className="sm:col-span-2 h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Select value={newCert.cert_type} onValueChange={(v) => setNewCert({ ...newCert, cert_type: v })}>
                  <SelectTrigger data-testid="new-cert-type" className="h-9 bg-zinc-900 border-zinc-800 text-sm"><SelectValue /></SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-zinc-800">{CERT_TYPES.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
                </Select>
                <Input data-testid="new-cert-node" value={newCert.node} onChange={(e) => setNewCert({ ...newCert, node: e.target.value })} placeholder="Node" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Input data-testid="new-cert-cn" value={newCert.issued_to} onChange={(e) => setNewCert({ ...newCert, issued_to: e.target.value })} placeholder="CN" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
                <Input data-testid="new-cert-expires" value={newCert.expires} onChange={(e) => setNewCert({ ...newCert, expires: e.target.value })} placeholder="YYYY-MM-DD" className="h-9 bg-zinc-900 border-zinc-800 text-sm" />
              </div>
              <div className="flex justify-end gap-2 mt-3">
                <button onClick={() => setAdding(false)} className="px-3 py-1.5 rounded-md bg-zinc-800 hover:bg-zinc-700 text-sm">Cancel</button>
                <button onClick={addCert} data-testid="confirm-add-cert" className="px-3 py-1.5 rounded-md bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium">Add Certificate</button>
              </div>
            </div>
          ) : (
            <button onClick={() => setAdding(true)} data-testid="add-cert-btn" className="w-full flex items-center justify-center gap-2 px-4 py-3 border-t border-zinc-800 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800/40 transition-colors">
              <Plus className="h-4 w-4" /> Add certificate
            </button>
          )}
        </div>

        <p className="text-[12px] text-zinc-600 mt-4 font-mono">
          Tip: list all certs with <span className="text-zinc-400">show cert list own</span> from the CLI, or check
          <span className="text-zinc-400"> Cisco Unified OS Administration &gt; Security &gt; Certificate Management</span>.
        </p>
      </div>
    </div>
  );
}

const SummaryStat = ({ label, value, testId, tone = "default" }) => {
  const cls = tone === "red" ? "text-red-400" : tone === "amber" ? "text-amber-400" : "text-white";
  return (
    <div className="rounded-lg border border-zinc-800 bg-zinc-900 p-4" data-testid={testId}>
      <div className={`font-heading text-3xl font-extrabold ${cls}`}>{value}</div>
      <div className="text-[11px] uppercase tracking-widest text-zinc-500 font-semibold mt-1">{label}</div>
    </div>
  );
};
